import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, TextField, Grid, CircularProgress } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useUserContext } from '../../hooks/useUserContext';
import { getApiErrorInfo } from '../../services/api';
import { tenantService } from '../../services/tenantService';
import { userService } from '../../services/userService';
import { track } from '../../services/analytics';
import {
  AuthPageLayout,
  LeftBrandContainer,
  LeftBrandContent,
  RightContentArea,
  FormContainer,
  FormHeader,
  ErrorDisplay,
  NameFields
} from '../../components/auth/SharedAuthComponents';

// Types for business setup data
interface BusinessSetupData {
  companyName: string;
  firstName: string;
  lastName: string;
}

// Business Setup Step Component
function BusinessSetupStep({
  onNext,
  data,
  isCreatingBusiness,
  businessCreationError,
  fieldErrors
}: {
  onNext: (data: BusinessSetupData) => void;
  data: BusinessSetupData;
  isCreatingBusiness: boolean;
  businessCreationError: string | null;
  fieldErrors: { companyName?: string; firstName?: string; lastName?: string };
}) {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    onNext({
      companyName: (formData.get('companyName') as string).trim(),
      firstName: (formData.get('firstName') as string).trim(),
      lastName: (formData.get('lastName') as string).trim(),
    });
  };

  return (
    <FormContainer>
      <FormHeader
        title="Set Up Your Business Account"
        subtitle="Create your organization and admin account"
        icon={
          <BusinessIcon
            sx={{
              fontSize: 48,
              color: 'primary.main',
              bgcolor: 'primary.light',
              borderRadius: '50%',
              p: 1.5,
            }}
          />
        }
      />

      <ErrorDisplay
        error={businessCreationError}
        onRetry={() => window.location.reload()}
      />

      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <TextField
              name="companyName"
              label="Company/Business Name"
              defaultValue={data.companyName || ''}
              required
              fullWidth
              variant="outlined"
              helperText={fieldErrors.companyName || 'This will be the name of your organization'}
              error={Boolean(fieldErrors.companyName)}
            />
          </Grid>
          <NameFields
            firstNameValue={data.firstName}
            lastNameValue={data.lastName}
            firstNameLabel="Admin First Name"
            lastNameLabel="Admin Last Name"
            firstNameError={fieldErrors.firstName}
            lastNameError={fieldErrors.lastName}
          />
        </Grid>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={isCreatingBusiness}
          sx={{ mt: 4, py: 1.5, fontSize: 16 }}
        >
          {isCreatingBusiness ? 'Creating Business Account...' : 'Create Business Account'}
        </Button>
      </Box>
    </FormContainer>
  );
}



export default function BusinessSetupPage() {
  const { clerkUser, isLoaded, isSignedIn, refreshUser } = useUserContext();
  const navigate = useNavigate();

  // Initialization: ensure backend user record exists before showing the form
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const hasInitialized = useRef(false);

  const [isCreatingBusiness, setIsCreatingBusiness] = useState(false);
  const [businessCreationError, setBusinessCreationError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ companyName?: string; firstName?: string; lastName?: string }>({});
  const [businessData, setBusinessData] = useState<BusinessSetupData>({
    companyName: '',
    firstName: '',
    lastName: '',
  });

  // Redirect if not signed in once Clerk has loaded
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/auth/login', { replace: true });
    }
  }, [isLoaded, isSignedIn, navigate]);

  // Create backend user record (get-or-create) then reveal the form
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !clerkUser || hasInitialized.current) return;
    hasInitialized.current = true;

    const init = async () => {
      try {
        await userService.createBackendAccount({
          authProviderId: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress || '',
          firstName: clerkUser.firstName || '',
          lastName: clerkUser.lastName || '',
        });
        setBusinessData({
          companyName: '',
          firstName: clerkUser.firstName || '',
          lastName: clerkUser.lastName || '',
        });
      } catch (error: unknown) {
        const apiError = getApiErrorInfo(error);
        setInitError(apiError.message || 'Unable to set up your account. Please try again.');
      } finally {
        setIsInitializing(false);
      }
    };

    init();
  }, [isLoaded, isSignedIn, clerkUser]);

  const handleBusinessNext = async (data: BusinessSetupData) => {
    const nextErrors: { companyName?: string; firstName?: string; lastName?: string } = {};
    if (!data.companyName) nextErrors.companyName = 'Company name is required';
    if (!data.firstName) nextErrors.firstName = 'First name is required';
    if (!data.lastName) nextErrors.lastName = 'Last name is required';

    if (nextErrors.companyName || nextErrors.firstName || nextErrors.lastName) {
      setFieldErrors(nextErrors);
      setBusinessCreationError(null);
      return;
    }

    try {
      setIsCreatingBusiness(true);
      setBusinessCreationError(null);
      setFieldErrors({});

      if (clerkUser) {
        await tenantService.createBusinessTenant({
          companyName: data.companyName,
          adminUser: {
            authProviderId: clerkUser.id,
            email: clerkUser.primaryEmailAddress?.emailAddress || '',
            firstName: data.firstName,
            lastName: data.lastName,
          }
        });

        // Refresh backend profile so backendProfile.tenantId is populated for the billing page
        await refreshUser();

        track('business_setup_completed');
        navigate('/app');
      } else {
        throw new Error('User authentication is required to create business account');
      }

    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('Failed to create business account:', error);
      setBusinessCreationError(apiError.message || 'Unable to create your business account. Please try again.');
    } finally {
      setIsCreatingBusiness(false);
    }
  };

  // Show spinner while Clerk loads or backend account is being initialized
  if (!isLoaded || isInitializing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error if initialization failed
  if (initError) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h6" color="error" gutterBottom>Failed to set up account</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{initError}</Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>Retry</Button>
        </Box>
      </Box>
    );
  }

  return (
    <AuthPageLayout>
      <LeftBrandContainer>
        <LeftBrandContent
          title="Create your business organization"
          subtitle="Set up your company and become the admin"
        />
      </LeftBrandContainer>

      <RightContentArea>
        <BusinessSetupStep
          onNext={handleBusinessNext}
          data={businessData}
          isCreatingBusiness={isCreatingBusiness}
          businessCreationError={businessCreationError}
          fieldErrors={fieldErrors}
        />
      </RightContentArea>
    </AuthPageLayout>
  );
}
